import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { snapshotStore } from '@/lib/crm-snapshot-store';
import { clearQueryCachesForUserChange } from '@/lib/query-client';

/** localStorage key used to detect user-id changes between sessions. When the
 *  signed-in user.id differs from this, we wipe the CRM snapshot IDB store
 *  before any cached data can leak across tenants on a shared device. */
const LAST_USER_ID_KEY = 'ob.lastUserId';

/**
 * True when the session came from a real login (password or Google), false
 * when it came from a recovery / invite link. Supabase records how a session
 * was obtained in the access token's `amr` claim: "password", "oauth" for
 * logins; "otp" (and "magiclink" / "recovery") for email links. Opening an
 * invite link signs the person in before they have chosen a password, so
 * invitation_accepted_at must not be stamped on that session; Auth.tsx stamps
 * it once the password is saved. Decoding here (not a flag in storage) keeps
 * the decision correct in every tab that receives the SIGNED_IN event.
 */
export function isFullLogin(session: Session): boolean {
  try {
    const payload = session.access_token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const amr: Array<{ method?: string }> = JSON.parse(json)?.amr ?? [];
    return amr.some((entry) => entry.method === 'password' || entry.method === 'oauth');
  } catch {
    // Unreadable token: treat as a real login (pre-Oct-2026 behaviour).
    return true;
  }
}

type AppRole = 'owner' | 'admin' | 'developer' | 'manager' | 'member' | 'viewer' | 'client';

export interface Profile {
  id: string;
  email: string | null;
  full_name: string | null;
  company_name: string | null;
  role: AppRole;
  client_id: string | null;
  /** Array of clients.id UUIDs this user has access to */
  client_ids: string[];
  /** Granular permissions (e.g. { campaigns: { view: true, manage: false } }) */
  permissions: Record<string, any>;
  /** Set once the invitee has saved a password (Auth.tsx); null while the invite is pending. */
  invitation_accepted_at?: string | null;
}

/** Numeric levels for role hierarchy comparison (higher = more privileged) */
const ROLE_LEVELS: Record<AppRole, number> = {
  owner: 100,
  admin: 80,
  developer: 80,
  manager: 60,
  member: 50,
  viewer: 40,
  client: 20,
};

/** Check if a role meets a minimum required level */
function hasMinRole(userRole: AppRole | undefined | null, minRole: AppRole): boolean {
  if (!userRole) return false;
  return (ROLE_LEVELS[userRole] ?? 0) >= (ROLE_LEVELS[minRole] ?? 999);
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName: string, companyName?: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  /** true for owner, admin, developer */
  isAdmin: boolean;
  isClient: boolean;
  isOwner: boolean;
  isDeveloper: boolean;
  isManager: boolean;
  isViewer: boolean;
  /** Check if the current user's role meets a minimum role level */
  hasMinRole: (minRole: AppRole) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // Track whether we've already fetched the profile to avoid duplicate fetches
  // from both onAuthStateChange and getSession firing on mount
  const profileFetchedRef = React.useRef<string | null>(null);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  };

  useEffect(() => {
    let mounted = true;

    // Helper: fetch profile only once per user ID
    const loadProfile = (userId: string, isSignIn = false) => {
      if (profileFetchedRef.current === userId) return; // Already fetched for this user
      profileFetchedRef.current = userId;

      setTimeout(async () => {
        if (!mounted) return;
        const profileData = await fetchProfile(userId);
        if (mounted && profileData) {
          // Stamp invitation_accepted_at only on actual sign-in (not token refresh/page load)
          if (isSignIn && !(profileData as any).invitation_accepted_at) {
            await supabase
              .from('profiles')
              .update({ invitation_accepted_at: new Date().toISOString() } as any)
              .eq('id', userId);
            (profileData as any).invitation_accepted_at = new Date().toISOString();
          }
          setProfile({
            ...profileData,
            client_ids: profileData.client_ids || [],
            permissions: profileData.permissions || {},
          });
          setLoading(false);
        } else if (mounted) {
          setProfile(null);
          setLoading(false);
        }
      }, 0);
    };

    // Set up auth state listener — handles sign-in, sign-out, token refresh
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // CRITICAL DATA-LEAK GUARD: if the signed-in user differs from the
          // last user we cached for, wipe the CRM snapshot IDB store before
          // any cached rows from the prior user could be rendered. Defends
          // against shared-device sign-in flows that bypass our signOut().
          try {
            const lastUserId = localStorage.getItem(LAST_USER_ID_KEY);
            if (lastUserId && lastUserId !== session.user.id) {
              snapshotStore.clearAll().catch(() => {});
              // Same guard for the React-Query layer: wipe the in-memory
              // cache AND the localStorage-persisted copy so the previous
              // user's query results (client lists, dashboard tiles) are
              // never served to this user. Found 2026-07-29: the persisted
              // cache survived a client → admin re-login for its full
              // staleTime, and the reverse direction leaks admin data.
              clearQueryCachesForUserChange();
            }
            localStorage.setItem(LAST_USER_ID_KEY, session.user.id);
          } catch {
            // localStorage can throw in private mode — ignore, the signOut
            // wipe + per-row RLS check still protect us.
          }

          // On SIGNED_OUT → SIGNED_IN, reset ref so we fetch the new user's profile
          if (event === 'SIGNED_IN') {
            profileFetchedRef.current = null;
          }
          loadProfile(session.user.id, event === 'SIGNED_IN' && isFullLogin(session));
        } else {
          profileFetchedRef.current = null;
          setProfile(null);
          setLoading(false);
        }
      }
    );

    // Check for existing session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, fullName: string, companyName?: string) => {
    // Defense-in-depth: restrict sign-up to @outboundbuddy.com domain
    const domain = email.split('@')[1]?.toLowerCase();
    if (domain !== 'outboundbuddy.com') {
      return { error: { message: 'Sign-up is restricted to invited team members only.' } };
    }

    const redirectUrl = `${window.location.origin}/`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          company_name: companyName,
        }
      }
    });
    return { error };
  };

  const signInWithGoogle = async () => {
    // After Google completes, Supabase redirects to this URL.
    // The /auth page picks up the session via onAuthStateChange and navigates onward.
    const redirectTo = `${window.location.origin}/auth`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        queryParams: {
          // Force account chooser so users can switch accounts
          prompt: 'select_account',
        },
      },
    });
    return { error };
  };

  const signOut = async () => {
    // CRITICAL DATA-LEAK GUARD: wipe CRM snapshot IDB store BEFORE the
    // Supabase sign-out so a fast subsequent sign-in by another user on a
    // shared device can't briefly render the prior user's cached rows.
    try {
      await snapshotStore.clearAll();
    } catch {
      // Don't block sign-out on cache cleanup failures.
    }
    // Wipe React-Query caches (in-memory + persisted) at sign-out too, so
    // the next sign-in on this browser starts from zero even if it happens
    // in a flow that skips the user-change guard above.
    clearQueryCachesForUserChange();
    try {
      localStorage.removeItem(LAST_USER_ID_KEY);
    } catch {
      // ignore localStorage errors
    }
    // 2026-09-03 (Sabeeh): on the live app the Logout button did nothing —
    // the server answered the global sign-out with 403 and the session
    // stayed in storage, so the user remained on the dashboard. Whatever
    // the server says, the local session must go: if one is still there
    // after signOut(), drop the stored token ourselves and hard-navigate
    // to the sign-in page so every provider re-initialises empty.
    let signOutError: unknown = null;
    try {
      const { error } = await supabase.auth.signOut();
      signOutError = error;
    } catch (e) {
      signOutError = e;
    }
    let stillSignedIn = false;
    try {
      const { data } = await supabase.auth.getSession();
      stillSignedIn = !!data?.session;
    } catch {
      stillSignedIn = true;
    }
    if (signOutError || stillSignedIn) {
      try {
        for (const store of [window.localStorage, window.sessionStorage]) {
          for (const key of Object.keys(store)) {
            if (/^sb-.*-auth-token/.test(key)) store.removeItem(key);
          }
        }
      } catch {
        // storage may be unavailable — the navigation below still leaves the page
      }
      window.location.replace('/auth');
    }
  };

  const refreshProfile = async () => {
    if (user) {
      const profileData = await fetchProfile(user.id);
      setProfile(profileData ? {
        ...profileData,
        client_ids: profileData.client_ids || [],
        permissions: profileData.permissions || {},
      } : null);
    }
  };

  // Hierarchical role checks — owner/admin/developer all count as "admin-level"
  const role = profile?.role;
  const isOwner = role === 'owner';
  const isDeveloper = role === 'developer';
  const isManager = role === 'manager';
  const isViewer = role === 'viewer';
  const isClient = role === 'client';
  const isAdmin = hasMinRole(role, 'admin'); // true for owner, admin, developer

  const value = {
    user,
    session,
    profile,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    refreshProfile,
    isAdmin,
    isClient,
    isOwner,
    isDeveloper,
    isManager,
    isViewer,
    hasMinRole: (minRole: AppRole) => hasMinRole(role, minRole),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}